import { create } from 'zustand'
import {
  collection,
  doc,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  onSnapshot
} from 'firebase/firestore'
import {
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
  deleteObject
} from 'firebase/storage'
import { auth, db, storage } from '@/config/firebase'
import { derived } from '@/lib/storeHelpers'

const PDF_MAX_BYTES = 15 * 1024 * 1024
const GLOBAL_AUDIENCE = 'global'

function sortIssues (a, b) {
  const dateCmp = (b.issueDate || '').localeCompare(a.issueDate || '')
  if (dateCmp !== 0) return dateCmp
  return (a.order || 0) - (b.order || 0)
}

function storagePathFor (issueId, uid) {
  if (!issueId || !uid) {
    throw new Error('issueId and auth uid are required for newsletter upload')
  }
  return `newsletters/global/${uid}/${issueId}.pdf`
}

function issueMatchesHub (issue, hubType) {
  const audiences = issue.audiences || [GLOBAL_AUDIENCE]
  if (audiences.includes(GLOBAL_AUDIENCE)) return true
  if (!hubType) return true
  return audiences.includes(hubType)
}

export interface CreateIssueParams {
  title: string
  issueDate: string
  pdfFile: File | Blob | null
  isPublished?: boolean
  audiences?: string[]
}

export interface NewslettersState {
  issues: any[]
  loading: boolean
  // derived
  publishedIssues: any[]
  hasPublishedIssues: boolean
  // actions
  issuesForHub: (hubType?: string | null) => any[]
  subscribePublished: () => void
  subscribeAll: () => void
  stopListeners: () => void
  createIssue: (params: CreateIssueParams) => Promise<{ success: boolean, id?: string, error?: string }>
  updateIssue: (issueId: string, updates: any, pdfFile?: File | Blob | null) => Promise<{ success: boolean, error?: string }>
  deleteIssue: (issue: any) => Promise<{ success: boolean, error?: string }>
}

let unsubscribe: (() => void) | null = null
let publishedListenerCount = 0
let allListenerCount = 0

const releaseListener = () => {
  if (unsubscribe) {
    unsubscribe()
    unsubscribe = null
  }
}

const uploadPdf = async (file, issueId): Promise<string | null> => {
  if (!file) return null
  if (file.size > PDF_MAX_BYTES) {
    throw new Error('PDF must be smaller than 15 MB')
  }
  const uid = auth.currentUser?.uid
  if (!uid) {
    throw new Error('You must be signed in to upload a newsletter PDF.')
  }
  const path = storagePathFor(issueId, uid)
  const ref = storageRef(storage, path)
  await uploadBytes(ref, file, {
    contentType: file.type || 'application/pdf'
  })
  return getDownloadURL(ref)
}

export const useNewslettersStore = create<NewslettersState>()(
  derived<NewslettersState>((s) => {
    const publishedIssues = s.issues
      .filter((i) => i.isPublished === true)
      .sort(sortIssues)
    return {
      publishedIssues,
      hasPublishedIssues: publishedIssues.length > 0
    }
  })((set, get) => ({
    issues: [],
    loading: false,
    publishedIssues: [],
    hasPublishedIssues: false,

    issuesForHub: (hubType = null) =>
      get().publishedIssues.filter((i) => issueMatchesHub(i, hubType)),

    subscribePublished: () => {
      publishedListenerCount++
      if (unsubscribe || allListenerCount > 0) return
      try {
        const q = query(
          collection(db, 'newsletterIssues'),
          where('isPublished', '==', true)
        )
        unsubscribe = onSnapshot(
          q,
          (snap) => {
            set({ issues: snap.docs.map((d) => ({ id: d.id, ...d.data() })) })
          },
          (err) => console.error('Error loading newsletter issues:', err)
        )
      } catch (err) {
        console.error('Error subscribing to newsletters:', err)
      }
    },

    subscribeAll: () => {
      const firstAllSubscriber = allListenerCount === 0
      allListenerCount++
      if (!firstAllSubscriber) return
      releaseListener()
      try {
        unsubscribe = onSnapshot(
          collection(db, 'newsletterIssues'),
          (snap) => {
            set({ issues: snap.docs.map((d) => ({ id: d.id, ...d.data() }) as any).sort(sortIssues) })
          },
          (err) => console.error('Error loading newsletter issues:', err)
        )
      } catch (err) {
        console.error('Error subscribing to newsletters:', err)
      }
    },

    stopListeners: () => {
      if (allListenerCount > 0) {
        allListenerCount--
        if (allListenerCount > 0) return
      }
      if (publishedListenerCount > 0) {
        publishedListenerCount--
        if (publishedListenerCount > 0) return
      }
      releaseListener()
    },

    createIssue: async ({ title, issueDate, pdfFile, isPublished = true, audiences = [GLOBAL_AUDIENCE] }) => {
      const uid = auth.currentUser?.uid
      if (!uid) {
        return { success: false, error: 'Must be signed in' }
      }
      if (!title?.trim()) {
        return { success: false, error: 'Title is required' }
      }
      if (!issueDate) {
        return { success: false, error: 'Issue date is required' }
      }
      if (!pdfFile) {
        return { success: false, error: 'PDF file is required' }
      }

      set({ loading: true })
      try {
        const issueRef = doc(collection(db, 'newsletterIssues'))
        const issueId = issueRef.id
        const pdfDownloadUrl = await uploadPdf(pdfFile, issueId)
        const now = new Date().toISOString()

        await setDoc(issueRef, {
          title: title.trim(),
          issueDate,
          pdfStoragePath: storagePathFor(issueId, uid),
          pdfDownloadUrl,
          isPublished: isPublished === true,
          audiences: Array.isArray(audiences) && audiences.length ? audiences : [GLOBAL_AUDIENCE],
          order: Date.now(),
          createdAt: now,
          updatedAt: now,
          createdBy: uid
        })

        return { success: true, id: issueId }
      } catch (error) {
        console.error('createIssue', error)
        return { success: false, error: error.message }
      } finally {
        set({ loading: false })
      }
    },

    updateIssue: async (issueId, updates, pdfFile = null) => {
      const uid = auth.currentUser?.uid
      if (!uid) {
        return { success: false, error: 'Must be signed in' }
      }

      set({ loading: true })
      try {
        const payload: any = {
          ...updates,
          updatedAt: new Date().toISOString()
        }

        if (pdfFile) {
          payload.pdfDownloadUrl = await uploadPdf(pdfFile, issueId)
          payload.pdfStoragePath = storagePathFor(issueId, uid)
        }

        if (payload.title) payload.title = payload.title.trim()

        await updateDoc(doc(db, 'newsletterIssues', issueId), payload)
        return { success: true }
      } catch (error) {
        console.error('updateIssue', error)
        return { success: false, error: error.message }
      } finally {
        set({ loading: false })
      }
    },

    deleteIssue: async (issue) => {
      set({ loading: true })
      try {
        if (issue.pdfStoragePath) {
          try {
            await deleteObject(storageRef(storage, issue.pdfStoragePath))
          } catch (err) {
            console.warn('Could not delete newsletter PDF from storage:', err)
          }
        }
        await deleteDoc(doc(db, 'newsletterIssues', issue.id))
        return { success: true }
      } catch (error) {
        console.error('deleteIssue', error)
        return { success: false, error: error.message }
      } finally {
        set({ loading: false })
      }
    }
  }))
)
